import React, {useMemo} from "react";
import {Box, Text} from "@unibuddy/patron";
import {marked} from "marked";
import markedFootnote from "marked-footnote";
import DOMPurify from "dompurify";
import styled, {css, keyframes} from "styled-components";

import {Align} from "./ChatMessage";
import {chatTheme} from "../../../../utils/theme/chatTheme";
import {renderer} from "../../utils/markedRenderer";
import {AssistantConversationMessage} from "../../../../types/generated";
import {
    ConversationState,
    useAssistantConversationContext,
} from "../../providers/AssistantConversationProvider";

type ChatMessageTextProps = {
    align: Align;
    message: AssistantConversationMessage;
    isLast: boolean;
};

const blink = keyframes`
  0%, 100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
`;

const TextContainer = styled(Box)<{$isTyping?: boolean}>`
    & :first-child {
        margin-top: 0;
    }
    & :last-child {
        margin-bottom: 0;
    }

    ${({$isTyping}) =>
        $isTyping &&
        css`
            & :last-child {
                display: inline;
            }
        `}
`;

const Cursor = styled(Box)`
    animation: ${blink} 0.8s ease-in-out infinite;
`;

DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    if (node.tagName && node.tagName.toLowerCase() === "a") {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
    }
});

marked.use({renderer});
marked.use(markedFootnote());

const UserChatMessageText = React.memo(({message}: {message: AssistantConversationMessage}) => {
    return (
        <Text whiteSpace="pre-wrap" color={chatTheme?.chat?.message?.textColor}>
            {message.message}
        </Text>
    );
});

const AssistantChatMessageText = React.memo(
    ({message, isLast}: {message: AssistantConversationMessage; isLast: boolean}) => {
        const {conversationState} = useAssistantConversationContext();

        const sanitizedMessage = useMemo(() => {
            const parsedMarkdown = marked.parse(message.message);
            return DOMPurify.sanitize(parsedMarkdown);
        }, [message.message]);

        return (
            <Text whiteSpace="pre-wrap">
                <Box position="relative">
                    <TextContainer
                        color={chatTheme?.chat?.message?.altTextColor}
                        dangerouslySetInnerHTML={{__html: sanitizedMessage}}
                        display="inline"
                        $isTyping={conversationState === ConversationState.ASSISTANT_IS_TYPING}
                    />
                    <Cursor
                        data-testid="assistant-typing-cursor"
                        display={
                            isLast && conversationState === ConversationState.ASSISTANT_IS_TYPING
                                ? "inline-block"
                                : "none"
                        }
                        marginLeft="xsmall"
                        width="12px"
                        height="12px"
                        bgColor={chatTheme?.chat?.message?.altTextColor}
                        borderRadius="full"
                    />
                </Box>
            </Text>
        );
    },
);

export const ChatMessageText = React.memo(({align, message, isLast}: ChatMessageTextProps) => {
    if (align === "right") {
        return <UserChatMessageText message={message} />;
    } else {
        return <AssistantChatMessageText message={message} isLast={isLast} />;
    }
});

UserChatMessageText.displayName = "UserChatMessageText";
AssistantChatMessageText.displayName = "AssistantChatMessageText";
ChatMessageText.displayName = "ChatMessageText";
